import { NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import { formaterNombre } from "../domaine/formats";
import { useSession } from "../session";
import { Marque } from "@cabinet/charte";
import { Barriere } from "./Barriere";

/**
 * Charpente de l'application : navigation à gauche, contenu à droite.
 *
 * Le tenant se choisit dans le menu et non dans chaque écran : c'est le cadre
 * de tout ce qui suit, au même titre que le dossier dans l'outil fiscal. En
 * changer vide le cache : rien de ce qui a été lu dans l'ancien tenant ne
 * survit au passage.
 */
export function MiseEnPage() {
  const { session, changerDeTenant, deconnecter, peut } = useSession();
  const { code, id: dossier } = useParams();
  // Changer de page réarme la barrière : une erreur passée ne doit pas
  // condamner l'écran suivant.
  const location = useLocation();

  const { data: referentiels } = useQuery({
    queryKey: ["referentiels"],
    queryFn: api.referentiels,
  });

  const courant = referentiels?.find((r) => r.code === code) ?? referentiels?.[0];

  return (
    <div className="application">
      <nav className="laterale">
        <Marque
          nom="Comptabilité"
          detail={
            session != null
              ? `${session.tenant.raisonSociale}${session.tenant.type ? ` — ${session.tenant.type}` : ""}`
              : "Aucun dossier"
          }
        />

        {session != null && session.tenantsAccessibles.length > 1 && (
          <select
            className="selecteur-referentiel"
            value={session.tenant.id}
            onChange={(e) => void changerDeTenant(e.target.value)}
            aria-label="Tenant"
          >
            {session.tenantsAccessibles.map((t) => (
              <option key={t.id} value={t.id}>
                {t.raisonSociale}
              </option>
            ))}
          </select>
        )}

        <div className="groupe">Portefeuille</div>
        <NavLink to="/dossiers" end className={({ isActive }) => (isActive ? "actif" : "")}>
          Dossiers
        </NavLink>

        {/* Le dossier ouvert donne accès à sa tenue : les livres se lisent
            dans son cadre, jamais hors de lui. */}
        {dossier != null && (
          <>
            <div className="groupe">Dossier</div>
            <NavLink to={`/dossiers/${dossier}`} end className={({ isActive }) => (isActive ? "actif" : "")}>
              Fiche
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/ecritures`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Écritures
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/import`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Import d'écritures
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/grand-livre`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Grand livre
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/balance`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Balance
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/lettrage`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Lettrage
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/plan`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Plan du dossier
            </NavLink>

            {/* Les achats : le référentiel des tiers, la saisie assistée d'une
                facture, et ce qu'elle laisse à payer. */}
            <div className="groupe">Achats</div>
            <NavLink to={`/dossiers/${dossier}/fournisseurs`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Fournisseurs
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/facture-achat`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Facture d'achat
            </NavLink>
            <NavLink to={`/dossiers/${dossier}/echeancier`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Échéancier
            </NavLink>

            <div className="groupe">Trésorerie</div>
            <NavLink to={`/dossiers/${dossier}/banque`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Banque
            </NavLink>

            {/* La fiscalité : la déclaration se calcule depuis les écritures
                validées, et se fige à la validation. */}
            <div className="groupe">Fiscalité</div>
            <NavLink to={`/dossiers/${dossier}/tva`} className={({ isActive }) => (isActive ? "actif" : "")}>
              Déclarations de TVA
            </NavLink>
          </>
        )}

        <div className="groupe">Référentiels</div>
        <NavLink to="/referentiels" className={({ isActive }) => (isActive ? "actif" : "")}>
          Référentiels chargés
          {referentiels != null && (
            <span className="compteur">{formaterNombre(referentiels.length)}</span>
          )}
        </NavLink>
        <NavLink
          to={`/plan/${courant?.code ?? "PCGM"}`}
          className={({ isActive }) => (isActive ? "actif" : "")}
        >
          Plan comptable
          {courant != null && (
            <span className="compteur">{formaterNombre(courant.nombreDeComptes)}</span>
          )}
        </NavLink>

        {(peut("GERER_UTILISATEURS") || peut("ADMINISTRER_PLATEFORME") || peut("EXPORTER_DONNEES")) && (
          <div className="groupe">Administration</div>
        )}
        {peut("GERER_UTILISATEURS") && (
          <NavLink to="/administration/utilisateurs" className={({ isActive }) => (isActive ? "actif" : "")}>
            Utilisateurs
          </NavLink>
        )}
        {peut("EXPORTER_DONNEES") && (
          <NavLink to="/audit" className={({ isActive }) => (isActive ? "actif" : "")}>
            Piste d'audit
          </NavLink>
        )}
        {peut("ADMINISTRER_PLATEFORME") && (
          <NavLink to="/plateforme" className={({ isActive }) => (isActive ? "actif" : "")}>
            Plateforme
          </NavLink>
        )}

        <div style={{ flex: 1 }} />

        <div className="pied-laterale">
          <NavLink to="/profil" className="lien-profil">
            {session?.utilisateur.nom}
          </NavLink>
          <br />
          <span className="compteur">{session?.roles.join(", ")} · </span>
          <a
            href="#deconnexion"
            onClick={(e) => {
              e.preventDefault();
              void deconnecter();
            }}
          >
            Déconnexion
          </a>
        </div>
      </nav>

      <main className="principale">
        {/* La barrière est posée autour du contenu et non de la charpente :
            une page en échec n'emporte pas la navigation avec elle. */}
        {/* Tant que le second facteur manque, les droits qui l'exigent sont
            retenus : le dire en tête de chaque écran, plutôt que de laisser
            l'utilisateur découvrir des boutons absents (EF-0103). */}
        {session != null && session.permissionsRetenues.length > 0 && location.pathname !== "/profil" && (
          <div className="bandeau-second-facteur">
            Valider, clôturer et déposer exigent l'authentification à deux facteurs.{" "}
            <NavLink to="/profil">L'activer</NavLink>
            {session.secondFacteurActif && " — ou se reconnecter avec son code"}.
          </div>
        )}
        <Barriere key={location.pathname}>
          <Outlet />
        </Barriere>
      </main>
    </div>
  );
}
